import Box from "@mui/material/Box";
import React, { MouseEvent } from "react";
import { Handle, NodeProps, NodeToolbar, Position } from "reactflow";
import { useAppSelector } from "../../hooks";
import {
  selectBlockIOPSSummary,
  selectDragHandleNode,
  selectOutputBlockTypes,
  selectRootPercentageSuccess,
} from "../../store/game/selectors";
import { removeSelectedBlock } from "../../store/game/gameSlice";
import { ROOT_BLOCK_ID, blockConfigs } from "../../models/block";
import CardContent from "@mui/material/CardContent";
import { BlockImage } from "./BlockImage";
import { IopsSummary } from "./IopsSummary";
import Fab from "@mui/material/Fab";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { BlockTypes } from "../../models/block-types";
import { Card } from "@mui/material";
import { useDispatch } from "react-redux";
import { BlockInfoDisplay } from "./BlockInfoDisplay";

export type ReactFlowBlockProps = {
  label: string;
  blockType: BlockTypes;
};

//A function that returns a boolean indicating if the current block can connect to the connectingNodeId
const canConnect = (
  connectingNodeId: string,
  connectingNodeType: BlockTypes,
  connectingNodeHandleType: string,
  currentNodeId: string,
  currentNodeType: BlockTypes,
  outputTypes: Map<string, number>,
  connectingOutputTypes: Map<string, number>
) => {
  if (connectingNodeId === currentNodeId) return false;
  if (connectingNodeHandleType === "source") {
    //connecting node is upstream
    const targetNodeAllowedTypes =
      blockConfigs[currentNodeType].allowedInputTypes;
    if (
      !targetNodeAllowedTypes ||
      !targetNodeAllowedTypes.has(connectingNodeType)
    ) {
      return false;
    }
    if (
      maxConnectionsReached(
        connectingOutputTypes,
        currentNodeType,
        connectingNodeType
      )
    )
      return false;
  } else {
    //connecting node is downstream
    const sourceNodeAllowedTypes =
      blockConfigs[currentNodeType].allowedOutputTypes;
    if (
      !sourceNodeAllowedTypes ||
      !sourceNodeAllowedTypes.has(connectingNodeType)
    ) {
      return false;
    }
    if (maxConnectionsReached(outputTypes, connectingNodeType, currentNodeType))
      return false;
  }
  return true;
};

const getBackgroundColor = (
  connectionIsHappening: boolean,
  enableHandles: boolean,
  currentNodeID: string,
  connectingNodeID: string | undefined
) => {
  if (!connectionIsHappening) return "white";
  if (connectingNodeID === currentNodeID) return "white";
  return enableHandles ? "green" : "red";
};

//a function that returns if the max connections for a block has been reached
const maxConnectionsReached = (
  outputConnectionCounts: Map<string, number>,
  connectingBlockType: BlockTypes,
  targetBlockType: BlockTypes
) => {
  const connectorLimits = blockConfigs[targetBlockType].outputConnectorLimits;
  if (!connectorLimits) return false;
  if (!outputConnectionCounts.has(connectingBlockType)) return false;
  const result =
    (outputConnectionCounts.get(connectingBlockType) || 0) >=
    connectorLimits[connectingBlockType];
  return result;
};

const ReactFlowBlock: React.FC<NodeProps<ReactFlowBlockProps>> = ({
  selected,
  data,
  id,
}) => {
  const dispatch = useDispatch();

  const connectingNode = useAppSelector((state) =>
    selectDragHandleNode(state.game)
  );
  const rootPercentageSuccess = useAppSelector((state) =>
    selectRootPercentageSuccess(state.game)
  );
  const isStarted = useAppSelector((state) => state.game.isStarted);
  const iopsSummary = useAppSelector((state) =>
    selectBlockIOPSSummary(state.game.blockIOPS, id)
  );
  const blockOutputBlockTypes = useAppSelector((state) =>
    selectOutputBlockTypes(state.game, id)
  );
  const connectingBlockOutputBlockTypes = useAppSelector((state) =>
    selectOutputBlockTypes(state.game, connectingNode?.nodeID)
  );
  const block = useAppSelector((state) => state.game.blocks[id]);
  const currentInputIOPS = useAppSelector(
    (state) => state.game.blockIOPS?.[id]?.inputIOPS
  );

  const connectionIsHappening = !!connectingNode;
  const enableHandles =
    !connectionIsHappening ||
    canConnect(
      connectingNode?.nodeID,
      connectingNode.blockType,
      connectingNode.type,
      id,
      data.blockType,
      blockOutputBlockTypes,
      connectingBlockOutputBlockTypes
    );

  const bgColor = getBackgroundColor(
    connectionIsHappening,
    enableHandles,
    id,
    connectingNode?.nodeID
  );

  const handleColor = connectionIsHappening ? "blue" : "#B9BBB9";

  const showInputConnector =
    enableHandles &&
    blockConfigs[data.blockType].allowedInputTypes &&
    blockConfigs[data.blockType].allowedInputTypes.size > 0;
  const showOutputConnector =
    enableHandles &&
    blockConfigs[data.blockType].allowedOutputTypes &&
    blockConfigs[data.blockType].allowedOutputTypes.size > 0;

  const canBeDeleted = id !== ROOT_BLOCK_ID;

  const width = 100;
  const height = 100;
  const containerWidth = selected ? width + 2 : width;
  const containerHeight = selected ? height + 2 : height;

  const handleBlockDelete = (
    e: MouseEvent<HTMLButtonElement>,
    blockID: string
  ) => {
    e.stopPropagation();
    dispatch(removeSelectedBlock(blockID));
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
      }}
    >
      <NodeToolbar isVisible={selected} position={Position.Left}>
        <Box
          style={{
            width: `${40}px`,
            height: `${40}px`,
            padding: "0px 5px",
            borderRadius: "20px",
          }}
        >
          {canBeDeleted && (
            <Fab
              color="primary"
              aria-label="add"
              size="small"
              style={{ transform: "scale(0.7)" }}
              onClick={(e) => {
                handleBlockDelete(e, id);
              }}
            >
              <DeleteOutlineIcon />
            </Fab>
          )}
        </Box>
      </NodeToolbar>
      <Box>
        <Handle
          type="target"
          position={Position.Top}
          isConnectable={showInputConnector}
          style={{
            visibility: showInputConnector ? "visible" : "hidden",
            left: 50,
            width: 20,
            height: 20,
            top: -10,
            backgroundColor: handleColor,
          }}
        />
        <Box
          style={{
            width: `${containerWidth}px`,
            height: `${containerHeight}px`,
            backgroundColor: " rgba(255, 255, 255, 0)",
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Card
            className="block"
            style={{
              width: `${width}px`,
              height: `${height}px`,
              border: selected ? "solid pink 2px" : "none",
              overflow: "visible",
              backgroundColor: bgColor,
            }}
          >
            <CardContent
              style={{
                padding: 0,
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                overflow: "visible",
                justifyContent: "center",
              }}
            >
              <Box>
                <BlockImage
                  width={80}
                  height={80}
                  blockType={data.blockType}
                  percentageSuccess={rootPercentageSuccess}
                />
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Handle
          type="source"
          position={Position.Bottom}
          id="b"
          isConnectable={showOutputConnector}
          style={{
            visibility: showOutputConnector ? "visible" : "hidden",
            left: 50,
            width: 20,
            height: 20,
            top: height - 10,
            backgroundColor: handleColor,
          }}
        ></Handle>
      </Box>
      {isStarted && (
        <Box
          style={{
            height: `${containerHeight}px`,
            width: "auto",
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <IopsSummary summary={iopsSummary} gameIsStarted={isStarted} />
          </Box>
          {selected && (
            <BlockInfoDisplay block={block} currentInputIOPS={currentInputIOPS} />
          )}
        </Box>
      )}
    </Box>
  );
};

export default ReactFlowBlock;
